/**
 * FormDropdownField is a dropdown field for forms that supports both single and multi-select.
 * Uses a custom dropdown UI with popover for better styling control.
 * Users can add up to 50 options, reorder them, and set defaults.
 *
 * In multi-select mode:
 * - Checkboxes are shown next to each option
 * - Multiple options can be selected
 * - Selected values are displayed as removable tags (first tag + "+N" count)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, useNode, type Node, ROOT_NODE } from '@craftjs/core';
import { cx } from '@emotion/css';
import type { NodeCustomProps } from '../../../types/craft';
import { useRenderContext } from '../../../context/RenderContext';
import { FORM_DROPDOWN_FIELD_NODE_NAME } from '../../../utils/constants';
import { generateUuidV4 } from '../form-utils';
import { setBreakpointProp } from '../../../utils/breakpointUtils';
import { getHelpTextStyle } from './fieldStyles';
import { ArrowDownIcon, CloseIcon, TickIcon } from './icons';
import { FormDropdownFieldPropsDto } from '../../../dto/craft/props/FormDropdownFieldPropsDto';

export type FormDropdownFieldProps = FormDropdownFieldPropsDto;

export const defaultProps: FormDropdownFieldProps = {
  id: '',
  label: 'Dropdown',
  showLabel: false,
  inputType: 'dropdown',
  required: false,
  hidden: false,
  urlMapping: '',
  placeholder: 'Select Option',
  options: ['Option one', 'Option two', 'Option three', 'Option four'],
  defaultIndices: [], // Single-select uses defaultIndices[0], multi-select uses all
  isSingleSelect: true, // Single-select by default
  helpText: '',
  showHelpText: false,
};

const DropdownCheckbox = ({ checked }: { checked: boolean }) => (
  <div
    className={cx('field-dropdown-checkbox', checked && 'field-dropdown-checkbox--checked')}
    aria-hidden="true"
  >
    <TickIcon className="field-dropdown-checkbox-icon" />
  </div>
);

const FormDropdownField = (props: Partial<FormDropdownFieldProps>) => {
  const fieldProps: FormDropdownFieldProps = { ...defaultProps, ...props };
  const { isEditable, environment } = useRenderContext();

  const {
    connectors: { connect, drag },
    id: craftNodeId,
    isLocked,
  } = useNode((node: Node) => ({
    parentId: node.data.parent as string | null,
    isLocked: node.data.custom?.isLocked,
  }));

  const { actions: editorActions, breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  // Dropdown state
  const [isOpen, setIsOpen] = useState(false);
  // Single-select state: use first index from defaultIndices
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    fieldProps.defaultIndices?.length > 0 ? fieldProps.defaultIndices[0] : null,
  );
  // Multi-select state: use all indices from defaultIndices
  const [selectedIndices, setSelectedIndices] = useState<number[]>(() => {
    return fieldProps.defaultIndices?.length > 0 ? fieldProps.defaultIndices : [];
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const tagRefsMap = useRef<Map<number, HTMLSpanElement>>(new Map());
  const [visibleTagsCount, setVisibleTagsCount] = useState(Infinity);

  // Track previous options to detect renames
  const prevOptionsRef = useRef<string[]>(fieldProps.options);

  // Check if multi-select mode is enabled
  const isMultiSelect = !fieldProps.isSingleSelect;

  // Check if we're in browser environment (for portal)
  const isServer = environment === 'server';

  // Sync selected state with defaults when switching modes (editor <-> preview) or when defaults change
  // This ensures preview/published mode always shows the configured defaults
  useEffect(() => {
    if (isMultiSelect) {
      setSelectedIndices(fieldProps.defaultIndices?.length > 0 ? fieldProps.defaultIndices : []);
    } else {
      // Single-select: use first index from defaultIndices
      setSelectedIndex(fieldProps.defaultIndices?.length > 0 ? fieldProps.defaultIndices[0] : null);
    }
  }, [fieldProps.defaultIndices, fieldProps.options, isEditable, isMultiSelect]);

  // With index-based selection, we don't need to sync on rename - indices stay valid
  // Just update prevOptionsRef for potential future use
  useEffect(() => {
    prevOptionsRef.current = fieldProps.options;
  }, [fieldProps.options]);

  // Calculate how many tags can fit in the container (multi-select mode)
  useEffect(() => {
    if (!isMultiSelect || selectedIndices.length === 0 || isServer) {
      setVisibleTagsCount(Infinity);
      return;
    }

    const calculateVisibleTags = () => {
      const trigger = triggerRef.current;
      if (!trigger) {
        setVisibleTagsCount(Infinity);
        return;
      }

      const triggerWidth = trigger.offsetWidth;
      const arrowWidth = 24; // Arrow icon + padding
      const containerPadding = 8; // padding
      const tagGap = 4;
      const availableWidth = triggerWidth - arrowWidth - containerPadding;

      // Measure actual tag widths from refs
      let totalWidth = 0;
      let count = 0;
      const selectedValues = selectedIndices.map((idx) => fieldProps.options[idx] || '');

      for (let i = 0; i < selectedValues.length; i++) {
        const tagEl = tagRefsMap.current.get(i);
        // Use actual width if available, otherwise estimate
        const tagWidth = tagEl?.offsetWidth || 60 + selectedValues[i].length * 6;
        const widthWithGap = tagWidth + (i > 0 ? tagGap : 0);

        // Check if we need badge space for remaining tags
        const remainingTags = selectedValues.length - (i + 1);
        const needsBadge = remainingTags > 0;
        // Badge width: ~28px for "+N"
        const badgeSpace = needsBadge ? 32 + tagGap : 0;
        const requiredWidth = totalWidth + widthWithGap + badgeSpace;

        if (requiredWidth <= availableWidth) {
          totalWidth += widthWithGap;
          count = i + 1;
        } else {
          break;
        }
      }

      setVisibleTagsCount(Math.max(1, count));
    };

    // ResizeObserver for container resize
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(calculateVisibleTags);
    });

    if (triggerRef.current) {
      resizeObserver.observe(triggerRef.current);
    }

    // Initial calc with delay
    const timer = setTimeout(calculateVisibleTags, 50);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timer);
    };
  }, [isMultiSelect, selectedIndices, isServer, fieldProps.options]);

  // Generate a unique ID if one isn't set (using useEffect to avoid render-time state updates)
  const hasGeneratedId = useRef(false);
  useEffect(() => {
    if (!fieldProps.id && !hasGeneratedId.current) {
      hasGeneratedId.current = true;
      const nodeId = generateUuidV4();
      editorActions.history.ignore().setProp(craftNodeId, (props: FormDropdownFieldProps) => {
        setBreakpointProp(props, 'id', nodeId, breakpoint, FORM_DROPDOWN_FIELD_NODE_NAME);
      });
    }
  }, [fieldProps.id, editorActions, craftNodeId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as globalThis.Node;
      const isOutsideTrigger = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(target);

      if (isOutsideTrigger && isOutsidePopover) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on scroll, zoom, or resize
  useEffect(() => {
    if (!isOpen) return;

    const handleClose = () => {
      setIsOpen(false);
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    const handleWheel = (e: WheelEvent) => {
      // Close on zoom (Ctrl/Cmd + wheel) or regular scroll outside popover
      const isZoom = e.ctrlKey || e.metaKey;
      if (isZoom) {
        setIsOpen(false);
        return;
      }

      // Close on scroll if scrolling outside the popover
      if (popoverRef.current && !popoverRef.current.contains(e.target as globalThis.Node)) {
        setIsOpen(false);
      }
    };

    // Listen for scroll on window and page container
    window.addEventListener('scroll', handleScroll, true); // Capture phase to catch all scrolls
    window.addEventListener('resize', handleClose);
    window.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleClose);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else if (hoveredIndex !== null) {
            if (isMultiSelect) {
              // Toggle selection in multi-select mode
              setSelectedIndices((prev) =>
                prev.includes(hoveredIndex)
                  ? prev.filter((i) => i !== hoveredIndex)
                  : [...prev, hoveredIndex],
              );
            } else {
              setSelectedIndex(hoveredIndex);
              setIsOpen(false);
            }
          }
          break;
        case 'Escape':
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHoveredIndex((prev) =>
              prev === null ? 0 : Math.min(prev + 1, fieldProps.options.length - 1),
            );
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (isOpen) {
            setHoveredIndex((prev) => (prev === null ? 0 : Math.max(prev - 1, 0)));
          }
          break;
        case 'Tab':
          if (isOpen) {
            setIsOpen(false);
          }
          break;
      }
    },
    [isOpen, hoveredIndex, fieldProps.options, isMultiSelect],
  );

  // Handle option click - different behavior for single vs multi-select
  const handleOptionClick = (optionIndex: number) => {
    if (isMultiSelect) {
      // Toggle selection in multi-select mode - don't close dropdown
      setSelectedIndices((prev) =>
        prev.includes(optionIndex) ? prev.filter((i) => i !== optionIndex) : [...prev, optionIndex],
      );
    } else {
      // Single select - select and close
      setSelectedIndex(optionIndex);
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  // Handle removing a single selected value (multi-select mode)
  const handleRemoveValue = (indexToRemove: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent dropdown from opening
    setSelectedIndices((prev) => prev.filter((i) => i !== indexToRemove));
  };

  const toggleDropdown = () => {
    if (!isLocked) {
      setIsOpen(!isOpen);
      setHoveredIndex(null);
    }
  };

  // Use fieldProps.id or 'temp' as fallback during initial render before useEffect sets the id
  const fieldId = fieldProps.id || 'temp';

  // Dropdowns should be interactive in the canvas (unlike text inputs)
  // Only disable pointer events when explicitly locked
  const inputDisablePointerEvents = isEditable && isLocked;

  // Get selected value as text (derived from index) - for single-select
  const selectedValue =
    selectedIndex !== null && selectedIndex < fieldProps.options.length
      ? fieldProps.options[selectedIndex]
      : '';

  // Get selected values as array of strings - for multi-select
  const selectedValues = selectedIndices
    .filter((idx) => idx < fieldProps.options.length)
    .map((idx) => fieldProps.options[idx]);

  // Display text and selection state (different for single vs multi-select)
  const hasSelection = isMultiSelect ? selectedIndices.length > 0 : selectedIndex !== null;
  const displayText = isMultiSelect
    ? selectedValues.length > 0
      ? selectedValues.join(', ')
      : fieldProps.placeholder
    : selectedValue || fieldProps.placeholder;

  // For hidden form input value
  const formInputValue = isMultiSelect ? selectedValues.join(',') : selectedValue;

  const renderPopover = () => {
    // Server render: always render popover (hidden) so controller can access options
    // Browser render: only render when open
    if (!isServer && !isOpen) return null;

    const popoverContent = (
      <div
        ref={popoverRef}
        className="field-dropdown-popover"
        role="listbox"
        aria-labelledby={fieldId}
        aria-multiselectable={isMultiSelect}
        style={isServer ? { display: 'none' } : undefined}
      >
        {fieldProps.options.map((option, index) => {
          // Check if option is selected (different for single vs multi-select)
          const isSelected = isMultiSelect
            ? selectedIndices.includes(index)
            : index === selectedIndex;
          const isHovered = index === hoveredIndex;

          return (
            <div
              key={index}
              className={cx(
                'field-dropdown-option',
                isSelected && 'field-dropdown-option--selected',
                isHovered && 'field-dropdown-option--hover',
              )}
              role="option"
              aria-selected={isSelected}
              onClick={() => handleOptionClick(index)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Show checkbox in multi-select mode */}
              {isMultiSelect && <DropdownCheckbox checked={isSelected} />}
              <span className="field-dropdown-option-text">{option}</span>
              {/* Show checkmark only in single-select mode when selected */}
              {!isMultiSelect && isSelected && <TickIcon className="field-dropdown-checkmark" />}
            </div>
          );
        })}
      </div>
    );

    return popoverContent;
  };

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cx(
        'field-node',
        'field-node--dropdown',
        isEditable && 'node-dashed-indicator styled-node',
        'boundary-indicator',
        fieldProps.hidden && 'field-node--hidden',
      )}
      data-field-id={fieldId}
      data-url-mapping={fieldProps.urlMapping || undefined}
      aria-hidden={fieldProps.hidden || undefined}
    >
      {/* Label (if showLabel is true) */}
      {fieldProps.showLabel && (
        <label className="field-label" htmlFor={fieldId}>
          {fieldProps.label}
          {fieldProps.required && (
            <span aria-hidden="true" className="field-required-indicator">
              *
            </span>
          )}
        </label>
      )}

      {/* Custom dropdown */}
      <div className="field-dropdown-wrapper" ref={dropdownRef} onKeyDown={handleKeyDown}>
        {/* Hidden native input for form submission */}
        <input
          type="hidden"
          id={fieldId}
          name={fieldId}
          value={formInputValue}
          required={fieldProps.required}
          data-required={fieldProps.required || undefined}
        />

        {/* Dropdown trigger button */}
        <button
          ref={triggerRef}
          type="button"
          className={cx(
            'field-dropdown-trigger',
            isOpen && 'field-dropdown-trigger--open',
            !hasSelection && 'field-dropdown-trigger--placeholder',
            isMultiSelect && hasSelection && 'field-dropdown-trigger--multiselect',
            inputDisablePointerEvents && 'pointer-events-none',
          )}
          onClick={toggleDropdown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-required={fieldProps.required}
          aria-describedby={
            fieldProps.showHelpText && fieldProps.helpText ? `${fieldId}-helper-text` : undefined
          }
          disabled={isLocked}
          data-placeholder={fieldProps.placeholder}
        >
          {/* Multi-select: show tags that fit + "+N" badge if needed */}
          {isMultiSelect && hasSelection ? (
            <div className="field-dropdown-tags" ref={tagsContainerRef}>
              {selectedIndices.slice(0, visibleTagsCount).map((idx, tagIndex) => (
                <span
                  key={idx}
                  className="field-dropdown-tag"
                  ref={(el) => {
                    if (el) tagRefsMap.current.set(tagIndex, el);
                    else tagRefsMap.current.delete(tagIndex);
                  }}
                >
                  <span className="field-dropdown-tag-text">{fieldProps.options[idx]}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="field-dropdown-tag-remove"
                    onClick={(e) => handleRemoveValue(idx, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRemoveValue(idx, e as unknown as React.MouseEvent);
                      }
                    }}
                    aria-label={`Remove ${fieldProps.options[idx]}`}
                  >
                    <CloseIcon className="field-dropdown-tag-remove-icon" />
                  </span>
                </span>
              ))}
              {visibleTagsCount < selectedIndices.length && (
                <span className="field-dropdown-tag-more">
                  +{selectedIndices.length - visibleTagsCount}
                </span>
              )}
            </div>
          ) : (
            <span className="field-dropdown-trigger-text" data-placeholder={fieldProps.placeholder}>
              {displayText}
            </span>
          )}
          <ArrowDownIcon />
        </button>

        {renderPopover()}
      </div>

      {/* Help text */}
      <div className={cx('field-helper-text-wrapper', getHelpTextStyle(fieldProps))}>
        <p id={`${fieldId}-helper-text`} className="field-helper-text">
          {fieldProps.showHelpText && fieldProps.helpText && fieldProps.helpText.trim().length > 0
            ? fieldProps.helpText
            : '\u200B'}
        </p>
      </div>
    </div>
  );
};

FormDropdownField.craft = {
  displayName: FORM_DROPDOWN_FIELD_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: 'Dropdown',
    isLocked: false,
    detachablePropsState: {
      desktop: {},
      tablet: {},
      mobile: {},
    },
  } as NodeCustomProps,
};

export default FormDropdownField;
